/*
 *
 *
 *
 *   Copyright (c) 2022. TinyMedic Pvt Ltd  - All Rights Reserved.
 *   This file is part of the API module  of the project: TinyMedic-Platform.
 *
 *
 *   Proprietary and confidential.
 *
 *   Unauthorized copying of this file, via any medium is strictly prohibited.
 *    Any resources of   TinyMedic-Platform can not be copied and/or distributed without the express permission of TinyMedic Pvt Ltd.
 *
 *
 *
 */

import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button, Col, Form, FormGroup, Input, InputGroup, Row, Table } from 'reactstrap';
import { JhiItemCount, JhiPagination, Translate, getPaginationState, translate } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faSortDown, faSortUp } from '@fortawesome/free-solid-svg-icons';
import { ASC, DESC, ITEMS_PER_PAGE, SORT } from 'app/shared/util/pagination.constants';
import { overridePaginationStateWithQueryParams } from 'app/shared/util/entity-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities, searchEntities } from './surgical-appointment.reducer';

export const SurgicalAppointment = () => {
  const dispatch = useAppDispatch();

  const pageLocation = useLocation();
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const [paginationState, setPaginationState] = useState(
    overridePaginationStateWithQueryParams(getPaginationState(pageLocation, ITEMS_PER_PAGE, 'id'), pageLocation.search),
  );

  const surgicalAppointmentList = useAppSelector(state => state.surgicalAppointment.entities);
  const loading = useAppSelector(state => state.surgicalAppointment.loading);
  const totalItems = useAppSelector(state => state.surgicalAppointment.totalItems);

  const getAllEntities = () => {
    if (search) {
      dispatch(
        searchEntities({
          query: search,
          page: paginationState.activePage - 1,
          size: paginationState.itemsPerPage,
          sort: `${paginationState.sort},${paginationState.order}`,
        }),
      );
    } else {
      dispatch(
        getEntities({
          page: paginationState.activePage - 1,
          size: paginationState.itemsPerPage,
          sort: `${paginationState.sort},${paginationState.order}`,
        }),
      );
    }
  };

  const startSearching = e => {
    if (search) {
      setPaginationState({
        ...paginationState,
        activePage: 1,
      });
      dispatch(
        searchEntities({
          query: search,
          page: paginationState.activePage - 1,
          size: paginationState.itemsPerPage,
          sort: `${paginationState.sort},${paginationState.order}`,
        }),
      );
    }
    e.preventDefault();
  };

  const clear = () => {
    setSearch('');
    setPaginationState({
      ...paginationState,
      activePage: 1,
    });
    dispatch(getEntities({}));
  };

  const handleSearch = event => setSearch(event.target.value);

  const sortEntities = () => {
    getAllEntities();
    const endURL = `?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`;
    if (pageLocation.search !== endURL) {
      navigate(`${pageLocation.pathname}${endURL}`);
    }
  };

  useEffect(() => {
    sortEntities();
  }, [paginationState.activePage, paginationState.order, paginationState.sort, search]);

  useEffect(() => {
    const params = new URLSearchParams(pageLocation.search);
    const page = params.get('page');
    const sort = params.get(SORT);
    if (page && sort) {
      const sortSplit = sort.split(',');
      setPaginationState({
        ...paginationState,
        activePage: +page,
        sort: sortSplit[0],
        order: sortSplit[1],
      });
    }
  }, [pageLocation.search]);

  const sort = p => () => {
    setPaginationState({
      ...paginationState,
      order: paginationState.order === ASC ? DESC : ASC,
      sort: p,
    });
  };

  const handlePagination = currentPage =>
    setPaginationState({
      ...paginationState,
      activePage: currentPage,
    });

  const handleSyncList = () => {
    sortEntities();
  };

  const getSortIconByFieldName = (fieldName: string) => {
    const sortFieldName = paginationState.sort;
    const order = paginationState.order;
    if (sortFieldName !== fieldName) {
      return faSort;
    }
    return order === ASC ? faSortUp : faSortDown;
  };

  return (
    <div>
      <h2 id="surgical-appointment-heading" data-cy="SurgicalAppointmentHeading">
        <Translate contentKey="tinyMedicRestApp.surgicalAppointment.home.title">Surgical Appointments</Translate>
        <div className="d-flex justify-content-end">
          <Button className="me-2" color="info" onClick={handleSyncList} disabled={loading}>
            <FontAwesomeIcon icon="sync" spin={loading} />{' '}
            <Translate contentKey="tinyMedicRestApp.surgicalAppointment.home.refreshListLabel">Refresh List</Translate>
          </Button>
          <Link
            to="/surgical-appointment/new"
            className="btn btn-primary jh-create-entity"
            id="jh-create-entity"
            data-cy="entityCreateButton"
          >
            <FontAwesomeIcon icon="plus" />
            &nbsp;
            <Translate contentKey="tinyMedicRestApp.surgicalAppointment.home.createLabel">Create new Surgical Appointment</Translate>
          </Link>
        </div>
      </h2>
      <Row>
        <Col sm="12">
          <Form onSubmit={startSearching}>
            <FormGroup>
              <InputGroup>
                <Input
                  type="text"
                  name="search"
                  defaultValue={search}
                  onChange={handleSearch}
                  placeholder={translate('tinyMedicRestApp.surgicalAppointment.home.search')}
                />
                <Button className="input-group-addon">
                  <FontAwesomeIcon icon="search" />
                </Button>
                <Button type="reset" className="input-group-addon" onClick={clear}>
                  <FontAwesomeIcon icon="trash" />
                </Button>
              </InputGroup>
            </FormGroup>
          </Form>
        </Col>
      </Row>
      <div className="table-responsive">
        {surgicalAppointmentList && surgicalAppointmentList.length > 0 ? (
          <Table responsive>
            <thead>
              <tr>
                <th className="hand" onClick={sort('id')}>
                  <Translate contentKey="tinyMedicRestApp.surgicalAppointment.id">Id</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('id')} />
                </th>
                <th className="hand" onClick={sort('actualStartTime')}>
                  <Translate contentKey="tinyMedicRestApp.surgicalAppointment.actualStartTime">Actual Start Time</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('actualStartTime')} />
                </th>
                <th className="hand" onClick={sort('actualEndTime')}>
                  <Translate contentKey="tinyMedicRestApp.surgicalAppointment.actualEndTime">Actual End Time</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('actualEndTime')} />
                </th>
                <th className="hand" onClick={sort('plannedStartTime')}>
                  <Translate contentKey="tinyMedicRestApp.surgicalAppointment.plannedStartTime">Planned Start Time</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('plannedStartTime')} />
                </th>
                <th className="hand" onClick={sort('plannedEndTime')}>
                  <Translate contentKey="tinyMedicRestApp.surgicalAppointment.plannedEndTime">Planned End Time</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('plannedEndTime')} />
                </th>
                <th className="hand" onClick={sort('status')}>
                  <Translate contentKey="tinyMedicRestApp.surgicalAppointment.status">Status</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('status')} />
                </th>
                <th className="hand" onClick={sort('bookedDate')}>
                  <Translate contentKey="tinyMedicRestApp.surgicalAppointment.bookedDate">Booked Date</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('bookedDate')} />
                </th>
                <th className="hand" onClick={sort('cancelledDate')}>
                  <Translate contentKey="tinyMedicRestApp.surgicalAppointment.cancelledDate">Cancelled Date</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('cancelledDate')} />
                </th>
                <th className="hand" onClick={sort('bookedByType')}>
                  <Translate contentKey="tinyMedicRestApp.surgicalAppointment.bookedByType">Booked By Type</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('bookedByType')} />
                </th>
                <th className="hand" onClick={sort('bookedById')}>
                  <Translate contentKey="tinyMedicRestApp.surgicalAppointment.bookedById">Booked By Id</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('bookedById')} />
                </th>
                <th className="hand" onClick={sort('cancelledByType')}>
                  <Translate contentKey="tinyMedicRestApp.surgicalAppointment.cancelledByType">Cancelled By Type</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('cancelledByType')} />
                </th>
                <th className="hand" onClick={sort('cancelledById')}>
                  <Translate contentKey="tinyMedicRestApp.surgicalAppointment.cancelledById">Cancelled By Id</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('cancelledById')} />
                </th>
                <th className="hand" onClick={sort('active')}>
                  <Translate contentKey="tinyMedicRestApp.surgicalAppointment.active">Active</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('active')} />
                </th>
                <th className="hand" onClick={sort('cursor')}>
                  <Translate contentKey="tinyMedicRestApp.surgicalAppointment.cursor">Cursor</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('cursor')} />
                </th>
                <th className="hand" onClick={sort('lastupdated')}>
                  <Translate contentKey="tinyMedicRestApp.surgicalAppointment.lastupdated">Lastupdated</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('lastupdated')} />
                </th>
                <th className="hand" onClick={sort('microSec')}>
                  <Translate contentKey="tinyMedicRestApp.surgicalAppointment.microSec">Micro Sec</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('microSec')} />
                </th>
                <th className="hand" onClick={sort('timestampentry')}>
                  <Translate contentKey="tinyMedicRestApp.surgicalAppointment.timestampentry">Timestampentry</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('timestampentry')} />
                </th>
                <th className="hand" onClick={sort('notes')}>
                  <Translate contentKey="tinyMedicRestApp.surgicalAppointment.notes">Notes</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('notes')} />
                </th>
                <th className="hand" onClick={sort('considerOnArrival')}>
                  <Translate contentKey="tinyMedicRestApp.surgicalAppointment.considerOnArrival">Consider On Arrival</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('considerOnArrival')} />
                </th>
                <th className="hand" onClick={sort('handedOverToId')}>
                  <Translate contentKey="tinyMedicRestApp.surgicalAppointment.handedOverToId">Handed Over To Id</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('handedOverToId')} />
                </th>
                <th className="hand" onClick={sort('handedOverToType')}>
                  <Translate contentKey="tinyMedicRestApp.surgicalAppointment.handedOverToType">Handed Over To Type</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('handedOverToType')} />
                </th>
                <th className="hand" onClick={sort('takenOverById')}>
                  <Translate contentKey="tinyMedicRestApp.surgicalAppointment.takenOverById">Taken Over By Id</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('takenOverById')} />
                </th>
                <th className="hand" onClick={sort('takenOverByType')}>
                  <Translate contentKey="tinyMedicRestApp.surgicalAppointment.takenOverByType">Taken Over By Type</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('takenOverByType')} />
                </th>
                <th>
                  <Translate contentKey="tinyMedicRestApp.surgicalAppointment.theatre">Theatre</Translate> <FontAwesomeIcon icon="sort" />
                </th>
                <th>
                  <Translate contentKey="tinyMedicRestApp.surgicalAppointment.surgery">Surgery</Translate> <FontAwesomeIcon icon="sort" />
                </th>
                <th />
              </tr>
            </thead>
            <tbody>
              {surgicalAppointmentList.map((surgicalAppointment, i) => (
                <tr key={`entity-${i}`} data-cy="entityTable">
                  <td>
                    <Button tag={Link} to={`/surgical-appointment/${surgicalAppointment.id}`} color="link" size="sm">
                      {surgicalAppointment.id}
                    </Button>
                  </td>
                  <td>{surgicalAppointment.actualStartTime}</td>
                  <td>{surgicalAppointment.actualEndTime}</td>
                  <td>{surgicalAppointment.plannedStartTime}</td>
                  <td>{surgicalAppointment.plannedEndTime}</td>
                  <td>
                    <Translate contentKey={`tinyMedicRestApp.CalendarStatus.${surgicalAppointment.status}`} />
                  </td>
                  <td>{surgicalAppointment.bookedDate}</td>
                  <td>{surgicalAppointment.cancelledDate}</td>
                  <td>
                    <Translate contentKey={`tinyMedicRestApp.AllottedByType.${surgicalAppointment.bookedByType}`} />
                  </td>
                  <td>{surgicalAppointment.bookedById}</td>
                  <td>
                    <Translate contentKey={`tinyMedicRestApp.AllottedByType.${surgicalAppointment.cancelledByType}`} />
                  </td>
                  <td>{surgicalAppointment.cancelledById}</td>
                  <td>{surgicalAppointment.active ? 'true' : 'false'}</td>
                  <td>{surgicalAppointment.cursor}</td>
                  <td>{surgicalAppointment.lastupdated}</td>
                  <td>{surgicalAppointment.microSec}</td>
                  <td>{surgicalAppointment.timestampentry}</td>
                  <td>{surgicalAppointment.notes}</td>
                  <td>{surgicalAppointment.considerOnArrival}</td>
                  <td>{surgicalAppointment.handedOverToId}</td>
                  <td>
                    <Translate contentKey={`tinyMedicRestApp.AddedByType.${surgicalAppointment.handedOverToType}`} />
                  </td>
                  <td>{surgicalAppointment.takenOverById}</td>
                  <td>
                    <Translate contentKey={`tinyMedicRestApp.AddedByType.${surgicalAppointment.takenOverByType}`} />
                  </td>
                  <td>
                    {surgicalAppointment.theatre ? (
                      <Link to={`/operation-theatre/${surgicalAppointment.theatre.id}`}>{surgicalAppointment.theatre.id}</Link>
                    ) : (
                      ''
                    )}
                  </td>
                  <td>
                    {surgicalAppointment.surgery ? (
                      <Link to={`/patient-procedure/${surgicalAppointment.surgery.id}`}>{surgicalAppointment.surgery.id}</Link>
                    ) : (
                      ''
                    )}
                  </td>
                  <td className="text-end">
                    <div className="btn-group flex-btn-group-container">
                      <Button
                        tag={Link}
                        to={`/surgical-appointment/${surgicalAppointment.id}`}
                        color="info"
                        size="sm"
                        data-cy="entityDetailsButton"
                      >
                        <FontAwesomeIcon icon="eye" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.view">View</Translate>
                        </span>
                      </Button>
                      <Button
                        tag={Link}
                        to={`/surgical-appointment/${surgicalAppointment.id}/edit?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`}
                        color="primary"
                        size="sm"
                        data-cy="entityEditButton"
                      >
                        <FontAwesomeIcon icon="pencil-alt" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.edit">Edit</Translate>
                        </span>
                      </Button>
                      <Button
                        onClick={() =>
                          (window.location.href = `/surgical-appointment/${surgicalAppointment.id}/delete?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`)
                        }
                        color="danger"
                        size="sm"
                        data-cy="entityDeleteButton"
                      >
                        <FontAwesomeIcon icon="trash" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.delete">Delete</Translate>
                        </span>
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          !loading && (
            <div className="alert alert-warning">
              <Translate contentKey="tinyMedicRestApp.surgicalAppointment.home.notFound">No Surgical Appointments found</Translate>
            </div>
          )
        )}
      </div>
      {totalItems ? (
        <div className={surgicalAppointmentList && surgicalAppointmentList.length > 0 ? '' : 'd-none'}>
          <div className="justify-content-center d-flex">
            <JhiItemCount page={paginationState.activePage} total={totalItems} itemsPerPage={paginationState.itemsPerPage} i18nEnabled />
          </div>
          <div className="justify-content-center d-flex">
            <JhiPagination
              activePage={paginationState.activePage}
              onSelect={handlePagination}
              maxButtons={5}
              itemsPerPage={paginationState.itemsPerPage}
              totalItems={totalItems}
            />
          </div>
        </div>
      ) : (
        ''
      )}
    </div>
  );
};

export default SurgicalAppointment;
